import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';

import {ApplicableTableSettings, mblStatus} from '../../../../tableSettings';

import IfmsDate from '../../../../ui/Date';
import TextBox from '../../../../ui/TextBox';
import CheckBox from '../../../../ui/CheckBox';
import {Time} from '../../../../ui/DateTime';
import SmartTable from '../../../../uiControls/SmartTable';
import OceanUnPortLookupField from '../../../../uiControls/lookup/OceanUnPortLookupField';
import {formatDate, formatDateTime, getTime, now} from '../../../../dateUtils';
import {PimsCntrEvent} from "../../../../fms.core/PimsCntrEvent";
import {DefaultContainerMilestoneStatus} from "./implementationConstants";
import { TableStorage } from '../../../../hooks/useTableStorage';
import { OeMbl } from '../../../../fms.core/OeMbl';

function SelectRenderer({data, row}: any) {
  if (!row.original.selectable) {
    return <div className="ifms-table-default"><CheckBox value={false} disabled dark/></div>;
  }

  const toggleRow = data[0].toggleRow;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x: any) => x === row.index) >= 0;

  return <div className="ifms-table-default"><CheckBox value={row.original.selectable && selected} disabled={!row.original.selectable} onChange={(check) => toggleRow(row.index, check)} /></div>;
}

function StatusNameRenderer({ cell }:any) {
  return <div style={{textAlign: 'left', marginLeft: '5px', paddingTop: '2px', fontSize: '11px'}}>{cell.value}</div>;
}

function DateRenderer({data, row}:any) {
  if (!row.original.selectable) {
    return <div style={{marginTop: '0px'}}><IfmsDate disabled value={row.original.date}/></div>;
  }

  const onRowChange = data[0].onRowChange;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x:any) => x === row.index) >= 0;

  function onChange(date: string|null) {
    if (!date) {
      onRowChange(row.index, {
        date: null,
        time: null,
        datetime: null,
      }, selected);
    } else {
      const tIndex = date.indexOf('T');
      const datetime = tIndex >= 0 ? `${date.substring(0, tIndex)}T${row.original.time}` : row.original.datetime;

      onRowChange(row.index, {
        date: date,
        datetime,
      }, selected);
    }
  }

  return <div style={{marginTop: '0px'}}><IfmsDate disabled={!selected} value={row.original.date} onChange={onChange} /></div>;
}

function TimeRenderer({data, row}:any) {
  if (!row.original.selectable) {
    return <div style={{marginTop: '0px'}}><TextBox disabled dark placeholder={'HH:MM'}
                                                    value={row.original.time || ''}/></div>;
  }

  const onRowChange = data[0].onRowChange;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x:any) => x === row.index) >= 0;
  
  function onChange(changedTime: string|null) {
    if (!row.original.date) {
      return;
    }

    const timeToApply = changedTime ?? '00:00';

    const tIndex = row.original.date.indexOf('T');
    const datetime = tIndex >= 0 ? `${row.original.date.substring(0, tIndex)}T${timeToApply}` : row.original.datetime;

    onRowChange(row.index, {
      time: timeToApply,
      datetime,
    }, selected);
  }

  return <div style={{marginTop: '0px'}}><Time disabled={!selected} value={row.original.time} onChange={onChange} /></div>;
}

function LocationRenderer({data, row}:any) {
  if (!row.original.selectable) {
    return <span></span>;
  }

  const onRowChange = data[0].onRowChange;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x:any) => x === row.index) >= 0;

  return selected
    ? <div style={{marginTop: '0px', width: '100%'}}>
      <OceanUnPortLookupField
        initialValue={row.original.locName ?? ''}
        onTextChange={locName => onRowChange(row.index, {locName}, selected)}
        onSelect={location => onRowChange(row.index, {locName: location.portName}, selected)}
        //searchBy="unWithPortName"
        searchBy="portName"
      />
    </div>
    : <div className="ifms-table-align-left">
      {row.original.locName ?? ''}
    </div>;
}

function LastEventTimeRenderer({row, data}:any) {
  return (
    <TextBox value={formatDateTime(row.original.lastDate)} disabled/>
  );
}

function MblMilestoneContainerStatus({
  selectedIndex,
  status,
  applyToAll,
  tableStorage,
  onChange,
  isView,
  mbl,
}:{
  selectedIndex: number,
  status: Array<PimsCntrEvent>|null,
  onChange: (rowIndex: number, status: Array<PimsCntrEvent>, select: boolean) => void,
  applyToAll: (status: Array<PimsCntrEvent>) => void,
  tableStorage: TableStorage,
  isView: boolean,
  mbl:OeMbl
}) {
  // const selectable = !!status;

  const [selectedIndeces, setSelectedIndeces] = useState<Array<any>>([]);

  useEffect(() => {
    setSelectedIndeces([]);
  }, [selectedIndex]);

  function toggleRow(rowIndex:any, select:boolean) {
    const index = selectedIndeces.findIndex(x => x === rowIndex);
    if (index >= 0) {
      const indeces = [...selectedIndeces];
      indeces.splice(index, 1);
      setSelectedIndeces(indeces);
      onRowChange(rowIndex, {
        check: false,
        date: null,
        time: null,
        datetime: null,
        locName: '',
      }, select);
    } else {
      setSelectedIndeces([...selectedIndeces, rowIndex]);

      const currentTime = now();
      let tempLocName = '';
      const time = getTime(currentTime);

      switch (rowIndex) {
        case 0: case 1: case 2: case 3: case 4:
          tempLocName = mbl.loadingPort;
          break;
        case 5: case 6: case 7: case 8:
          tempLocName = mbl.discharge;
          break;
        // case 5: case 6: 
        //   tempLocName = mbl.finalDestination;
        //   break;
        default:
          tempLocName = '';
          break;
      }

      onRowChange(rowIndex, {
        check: true,
        date: formatDate(currentTime),
        time,
        datetime: currentTime,
        locName: tempLocName
      }, select);
    }
  }

  function onRowChange(rowIndex:number, change:any, select:boolean) {
    if (!status) {
      return;
    }

    Object.assign(status[rowIndex], change);

    onChange(selectedIndex, [status[rowIndex]], select);
  }

  // function doApplyToAll(s:Array<PimsCntrEvent>|null) {
  //   if (s) {
  //     applyToAll(s);
  //   }
  // }
  const statusData = !!status ? status : DefaultContainerMilestoneStatus;

  const tableOptions:ApplicableTableSettings = {
    tableName: 'mblStatus',
    ...mblStatus,
    data: statusData.map((x:any, i:number) => ({...x, i, onRowChange, toggleRow, selectable: true, selectedIndeces, /* name: x.statusName */})),
    rowRenderers: [
      { accessor: 'check', Cell: SelectRenderer },
      { accessor: 'name', Cell: StatusNameRenderer },
      { accessor: 'date', Cell: DateRenderer },
      { accessor: 'time', Cell: TimeRenderer },
      { accessor: 'locName', Cell: LocationRenderer },
      { accessor: 'lastDate', Cell: LastEventTimeRenderer }
    ],
  };

  return (
    <div>
      <SmartTable {...tableOptions} />
    </div>
  );
}

MblMilestoneContainerStatus.propTypes = {
  selectedIndex: PropTypes.number,
  status: PropTypes.array,
  applyToAll: PropTypes.func,
  onChange: PropTypes.func,
  tableStorage: PropTypes.object,
  defaultStatus: PropTypes.any,
};

export default React.memo(MblMilestoneContainerStatus);
